import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EventIcon from '@mui/icons-material/Event';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { useState } from 'react';

import { useDataElTagGenerator } from '#src/utils/use-data-el-tag-generator.js';

import type { ReactElement } from 'react';

export interface DesignMonthGateDialogProps {
	open: boolean;
	/** The link that leaves the gate without contacting Cadstrom; defaults to "Back to projects". */
	closeLabel?: string;
	/** Called when the customer leaves the gate without contacting Cadstrom. */
	onClose: () => void;
	/** Where the customer can write to Cadstrom. */
	contactEmail: string;
	/** The booking page to embed for scheduling a call; null offers email only. */
	schedulingEmbedUrl: string | null;
	/** Called once the dialog has finished opening. */
	onShown?: () => void;
	/** Called when the customer opens the scheduler. */
	onSchedulerOpened?: () => void;
	/** Called when the customer chooses to email Cadstrom. */
	onEmailChosen?: () => void;
}

const buttonSx = { width: '100%', borderRadius: '8px' } as const;

/**
 * Tells the customer their account has no room for another design review project, and puts
 * them in touch with Cadstrom.
 *
 * @param props - The dialog's props.
 * @returns The gate dialog.
 */
export function DesignMonthGateDialog({
	open,
	closeLabel = 'Back to projects',
	onClose,
	contactEmail,
	schedulingEmbedUrl,
	onShown,
	onSchedulerOpened,
	onEmailChosen,
}: DesignMonthGateDialogProps): ReactElement {
	const el = useDataElTagGenerator(DesignMonthGateDialog);
	const [scheduling, setScheduling] = useState(false);
	const mailto = `mailto:${contactEmail}`;
	const showScheduler = scheduling && schedulingEmbedUrl !== null;

	return (
		<Dialog
			{...el()}
			open={open}
			onClose={onClose}
			aria-labelledby='design-month-gate-title'
			slotProps={{
				backdrop: {
					sx: { backgroundColor: 'color-mix(in oklab, var(--color-background), transparent 20%)' },
				},
				paper: {
					sx: {
						width: showScheduler ? '880px' : '552px',
						maxWidth: '100%',
						margin: 0,
						overflow: 'visible',
						backgroundColor: 'transparent',
						backgroundImage: 'none',
						boxShadow: 'none',
					},
				},
				transition: { onEntered: () => onShown?.(), onExited: () => setScheduling(false) },
			}}
		>
			<div className='flex flex-col gap-5 rounded-[14px] border border-gray-a5 bg-gray-2 p-[30px] shadow-[0_1px_0_oklch(from_var(--color-white)_l_c_h/0.04)_inset,0_24px_70px_oklch(from_var(--color-black)_l_c_h/0.55)]'>
				<div className='flex items-center gap-[9px]'>
					<img src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png' alt='' className='h-[22px] w-[22px] object-contain' />
					<span className='text-base font-semibold tracking-[0.16em] text-gray-a11 uppercase'>Cadstrom</span>
				</div>

				{showScheduler ?
					<>
						<div className='flex items-center gap-3'>
							<button
								type='button'
								aria-label='Back'
								onClick={() => setScheduling(false)}
								className='flex cursor-pointer items-center rounded-md p-1 text-gray-11 hover:bg-gray-a3'
							>
								<ArrowBackIcon fontSize='small' />
							</button>
							<div className='flex flex-col gap-1'>
								<h2 id='design-month-gate-title' className='text-2xl font-semibold text-gray-12'>
									Contact Cadstrom
								</h2>
								<p className='text-sm text-gray-11'>
									Book a call below, or email us at{' '}
									<a href={mailto} onClick={onEmailChosen} className='text-blue-11 underline underline-offset-3'>
										{contactEmail}
									</a>
									.
								</p>
							</div>
						</div>
						<iframe
							title='Schedule a call with Cadstrom'
							src={schedulingEmbedUrl}
							className='h-[600px] w-full rounded-[10px] border border-gray-a5 bg-gray-1'
						/>
					</>
				:	<>
						<h2
							id='design-month-gate-title'
							className='text-4xl font-semibold tracking-[-0.015em] text-balance text-gray-12'
						>
							Your account needs updating
						</h2>
						<p className='text-lg leading-[1.5] text-gray-11'>
							You have exceeded the available number of active design review projects.
						</p>
						<p className='text-sm leading-[1.5] text-gray-10'>
							Connect with our team to update your account and then pick right back up where you left off.
						</p>
						{schedulingEmbedUrl === null ?
							<Button
								variant='contained'
								startIcon={<EmailOutlinedIcon />}
								href={mailto}
								onClick={onEmailChosen}
								sx={{ ...buttonSx, paddingBlock: '13px', fontSize: 'var(--text-lg)', fontWeight: 600 }}
							>
								<span>Contact Cadstrom</span>
							</Button>
						:	<>
								<Button
									variant='contained'
									startIcon={<EventIcon />}
									onClick={() => {
										setScheduling(true);
										onSchedulerOpened?.();
									}}
									sx={{ ...buttonSx, paddingBlock: '13px', fontSize: 'var(--text-lg)', fontWeight: 600 }}
								>
									<span>Contact Cadstrom</span>
								</Button>
								<Button
									variant='outlined'
									startIcon={<EmailOutlinedIcon />}
									href={mailto}
									onClick={onEmailChosen}
									sx={{ ...buttonSx, paddingBlock: '10px', fontSize: 'var(--text-base)' }}
								>
									<span>Email {contactEmail}</span>
								</Button>
							</>
						}
						<div className='text-center'>
							<button
								type='button'
								onClick={onClose}
								className='cursor-pointer text-base text-blue-11 underline underline-offset-3 hover:text-blue-12'
							>
								{closeLabel}
							</button>
						</div>
					</>
				}
			</div>
		</Dialog>
	);
}
